import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'binderDeposito';
const USER_KEY = 'binderUser';

const UserIdContext = createContext({
  deposito: null,
  setDeposito: () => {},
  user: null,
  setUser: () => {},
  clearSession: () => {},
});

const readStoredDeposito = () => {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const readStoredUser = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const UserIdProvider = ({ children }) => {
  const [deposito, setDepositoState] = useState(() => readStoredDeposito());
  const [user, setUserState] = useState(() => readStoredUser());

  // Flags de rol disponibles en el contexto
  const role = (user?.rol || "").trim();
  const roleSlug = role.toLowerCase();
  const normalizedRole = roleSlug.startsWith("o") ? roleSlug.slice(1) : roleSlug;
  const isAdmin = normalizedRole === "administrador";
  const isFinanzas = normalizedRole === "visualizador finanzas";
  const isLogistica = normalizedRole === "visualizador logistica";

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (deposito) {
        localStorage.setItem(STORAGE_KEY, deposito);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      /* ignore storage errors */
    }
  }, [deposito]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (user) {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
      } else {
        localStorage.removeItem(USER_KEY);
      }
    } catch {
      /* ignore storage errors */
    }
  }, [user]);

  const clearSession = () => {
    setUserState(null);
    setDepositoState(null);
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('usuario');
    } catch {
      /* ignore storage errors */
    }
  };

  const value = useMemo(
    () => ({
      deposito,
      setDeposito: setDepositoState,
      user,
      setUser: setUserState,
      clearSession,
      role,
      roleSlug,
      isAdmin,
      isFinanzas,
      isLogistica,
    }),
    [deposito, user, role, roleSlug, isAdmin, isFinanzas, isLogistica]
  );

  return <UserIdContext.Provider value={value}>{children}</UserIdContext.Provider>;
};

export const useUserId = () => useContext(UserIdContext);
